import React, {Fragment} from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import moment from 'moment-tz';
import styled from 'styled-components';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {getElapsedTime} from 'app/general/dates';
import {
  BootstrapMaxSmallMediaQuery,
  BootstrapMinMediumMediaQuery,
} from 'app/general/mediaqueries';
import updateIntervalEnhancer, {
  type UpdateIntervalEnhancerProps,
} from 'app/general/updateIntervalEnhancer';
import {gt} from 'app/utils/i18n';

import type {NameEmailUserSerializedType} from 'sweet-types';

type GenericVersionType = {
  editor?: NameEmailUserSerializedType;
  created_at: string;
  is_current?: boolean;
  approved_at: string | null;
};
const StyledBarDIV = styled.div`
  padding: 5px 20px;
  text-align: center;
  transition: all 0.5s;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  border-top: 1px solid ${colors.borderGray};

  &.approved {
    background-color: rgba(222, 248, 251, 0.9);
  }
  &.draft {
    background-color: rgba(250, 250, 250, 0.9);
  }
  &.current {
    background-color: rgba(230, 255, 234, 0.9);
  }

  > * {
    margin: 5px 0;
  }

  .aditional-content {
    border-bottom: 1px dashed #999;
  }

  .togglediff-box {
    font-size: 0.8rem;
  }
`;
type Props = UpdateIntervalEnhancerProps & {
  className?: string;
  objectTypeName: string;
  version: GenericVersionType;
  showingDiff?: boolean;
  toggleDiff?: () => any;
};

const VersionInfoBar = ({
  className = '',
  objectTypeName,
  version,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  tickIndex,
  showingDiff,
  toggleDiff,
  ...other
}: Props) => {
  const buttonClassMap = {
    draft: 'secondary',
    current: 'success',
    approved: 'info',
  };

  function getDocumentTypeName(version) {
    return (
      <LazyTooltip
        tooltip={
          version.approved_at !== null
            ? version.is_current !== false
              ? gt(
                  'This is the approved version for the live %s.',
                  objectTypeName
                )
              : gt('This is an older approved version.')
            : gt('A manager must approve this draft before it goes live.')
        }
      >
        <b className="aditional-content">
          <i
            className="fa fa-info-circle"
            style={{
              marginRight: 5,
            }}
          />{' '}
          {version.approved_at !== null
            ? version.is_current !== false
              ? gt('Live version')
              : gt('Version')
            : gt('Draft')}
        </b>
      </LazyTooltip>
    );
  }

  function getApproverMessage(version) {
    if (version.approved_at == null) {
      return null;
    }

    const approverName =
      version.approved_by != null
        ? version.approved_by.name
        : gt('Deleted user');
    const elapsedTime = getElapsedTime(version.approved_at);
    const elapsedApprovalTimeComponent = (
      <LazyTooltip tooltip={moment(version.approved_at).format('lll')}>
        <span className="aditional-content">{elapsedTime}</span>
      </LazyTooltip>
    );
    const approvedWithTooltip = (
      <LazyTooltip tooltip={gt('by %s', approverName)}>
        <span className="aditional-content">{gt('approved')}</span>
      </LazyTooltip>
    );
    return (
      <Fragment>
        <BootstrapMinMediumMediaQuery>
          {gt('Approved by %s %s', approverName, elapsedApprovalTimeComponent)}
        </BootstrapMinMediumMediaQuery>
        <BootstrapMaxSmallMediaQuery>
          {gt('%s %s', approvedWithTooltip, elapsedApprovalTimeComponent)}
        </BootstrapMaxSmallMediaQuery>
      </Fragment>
    );
  }

  function getVersionBarMessage(version) {
    const editorName =
      version.editor != null ? version.editor.name : gt('Deleted user');
    const elapsedTime = getElapsedTime(version.created_at);
    const typeName = getDocumentTypeName(version);
    const elapsetTimeComponent = (
      <LazyTooltip tooltip={moment(version.created_at).format('lll')}>
        <span className="aditional-content">{elapsedTime}</span>
      </LazyTooltip>
    );
    const editedWithTooltip = (
      <LazyTooltip tooltip={gt('by %s', editorName)}>
        <span className="aditional-content">{gt('edited')}</span>
      </LazyTooltip>
    );
    return (
      <Fragment>
        <BootstrapMinMediumMediaQuery>
          {gt('%s edited by %s %s', typeName, editorName, elapsetTimeComponent)}
        </BootstrapMinMediumMediaQuery>
        <BootstrapMaxSmallMediaQuery>
          {gt('%s %s %s', typeName, editedWithTooltip, elapsetTimeComponent)}
        </BootstrapMaxSmallMediaQuery>
      </Fragment>
    );
  }

  let typeClass =
    version.approved_at !== null
      ? version.is_current === true
        ? 'current'
        : 'approved'
      : 'draft';
  return (
    <StyledBarDIV
      className={`d-flex flex-wrap align-items-center ${typeClass} ${className}`}
      {...other}
    >
      <div className="lead flex-grow-1 justify-content-center d-flex flex-wrap text-truncate">
        <div className="mx-2 text-truncate">
          {getVersionBarMessage(version)}
        </div>
        <div className="mx-2 text-truncate">{getApproverMessage(version)}</div>
      </div>
      {showingDiff != null && toggleDiff != null ? (
        <div className="togglediff-box ml-auto pl-3">
          <button
            className={`btn btn-sm btn-outline-${buttonClassMap[typeClass]}`}
            onClick={toggleDiff}
          >
            <i
              className={`far fa-fw fa-${
                showingDiff ? 'check-square' : 'square'
              }`}
            />{' '}
            {gt('Highlight Changes')}
          </button>
        </div>
      ) : null}
    </StyledBarDIV>
  );
};

export default updateIntervalEnhancer(VersionInfoBar, 60 * 1000);
